import { useSyncExternalStore } from "react";
import {
  type AppBuildMeta,
  fetchLatestAppBuild,
  isNewerAppBuild,
} from "@/lib/appVersion";

type AppVersionState = {
  latestBuild: AppBuildMeta | null;
  updateAvailable: boolean;
  lastCheckedAt: string | null;
};

const defaultState: AppVersionState = {
  latestBuild: null,
  updateAvailable: false,
  lastCheckedAt: null,
};

let appVersionState = defaultState;

const listeners = new Set<() => void>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function setAppVersionState(nextState: AppVersionState) {
  const hasChanged =
    nextState.latestBuild?.version !== appVersionState.latestBuild?.version ||
    nextState.latestBuild?.builtAt !== appVersionState.latestBuild?.builtAt ||
    nextState.updateAvailable !== appVersionState.updateAvailable ||
    nextState.lastCheckedAt !== appVersionState.lastCheckedAt;

  if (!hasChanged) {
    return;
  }

  appVersionState = nextState;
  emitChange();
}

export function getAppVersionState() {
  return appVersionState;
}

export function subscribeToAppVersion(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function checkForAppUpdate() {
  try {
    const latestBuild = await fetchLatestAppBuild();

    setAppVersionState({
      latestBuild,
      updateAvailable: isNewerAppBuild(latestBuild),
      lastCheckedAt: new Date().toISOString(),
    });
  } catch {
    setAppVersionState({
      ...appVersionState,
      lastCheckedAt: new Date().toISOString(),
    });
  }

  return appVersionState;
}

export function useAppVersionState() {
  return useSyncExternalStore(
    subscribeToAppVersion,
    getAppVersionState,
    getAppVersionState,
  );
}
